/**
 * Helper para dibujar retratos por código. La beta no usa assets
 * binarios, así que cada personaje se representa con un círculo de su
 * color (`Character.color`), un emoji grande centrado y el apodo debajo.
 * Reutilizable por cualquier escena que muestre hablantes (diálogos,
 * selección de compañero, etc.).
 *
 * Devuelve un `Container` con los tres GameObjects (círculo, emoji,
 * apodo). Si se le pasa un container existente, lo reutiliza y le añade
 * los hijos en lugar de crear uno nuevo — útil para escenas que ya
 * tienen un layout y quieren colocar el retrato sin un container extra.
 */

import Phaser from 'phaser';
import { getCharacter } from '@/data/personajes';

const RADIO = 40;
const GROSOR_BORDE = 3;
const COLOR_BORDE = 0xffffff;
const OPACIDAD_BORDE = 0.85;
const TAMANO_EMOJI = '40px';
const TAMANO_APODO = '16px';

export interface PortraitOptions {
  /** Si true, dibuja también el apodo debajo del círculo (defecto: true). */
  mostrarApodo?: boolean;
  /** Radio opcional para reutilizar el helper a otro tamaño. */
  radio?: number;
  /** Tamaño del emoji (defecto: 40px). */
  tamanoEmoji?: string;
}

/**
 * Dibuja un retrato centrado en (x, y) y devuelve el Container con sus
 * GameObjects. Si `parent` se pasa, el Container se añade dentro de él.
 * Si `emocion` viene, queda como hook para v2 (cuando haya arte real),
 * pero en la beta se ignora.
 */
export function drawPortrait(
  scene: Phaser.Scene,
  x: number,
  y: number,
  characterId: string,
  emocion?: string,
  options: PortraitOptions = {},
  parent?: Phaser.GameObjects.Container,
): Phaser.GameObjects.Container {
  const char = getCharacter(characterId);
  // emocion es hook de v2 — por ahora la beta solo tiene retrato neutro.
  void emocion;

  const radio = options.radio ?? RADIO;
  const mostrarApodo = options.mostrarApodo ?? true;
  const tamanoEmoji = options.tamanoEmoji ?? TAMANO_EMOJI;
  const color = char.color ?? 0x4a4a55; // gris neutro si no hay color definido
  const emoji = char.emoji ?? '👤';

  const container =
    parent ?? scene.add.container(x, y);

  // Círculo de color + borde blanco semitransparente.
  const circulo = scene.add.graphics();
  circulo.fillStyle(color, 1);
  circulo.fillCircle(0, 0, radio);
  circulo.lineStyle(GROSOR_BORDE, COLOR_BORDE, OPACIDAD_BORDE);
  circulo.strokeCircle(0, 0, radio);
  container.add(circulo);

  // Emoji grande centrado. El pequeño -4 compensa el baseline visual
  // típico de los emoji en navegadores, que se ven un pelo bajos.
  const textoEmoji = scene.add
    .text(0, -4, emoji, {
      fontFamily: 'system-ui, sans-serif',
      fontSize: tamanoEmoji,
    })
    .setOrigin(0.5);
  container.add(textoEmoji);

  // Apodo debajo del círculo.
  if (mostrarApodo) {
    const textoApodo = scene.add
      .text(0, radio + 20, char.apodo, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: TAMANO_APODO,
        fontStyle: 'bold',
        color: '#ffffff',
        align: 'center',
        wordWrap: { width: 160 },
      })
      .setOrigin(0.5);
    container.add(textoApodo);
  }

  return container;
}